import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import ConfiguratorLayout from '@src/routes/Configurators/components/ConfiguratorLayout'
import { standardTubeRoute, biTubeRoute } from '@src/routes'
import Step1 from '../Step1/Step1'
import {
  useProductFromQueryParam,
  getProductTitle,
  TabsEnum,
  getInitialValuesBasedOnProduct,
  ActiveLinkType,
  SingleTubeFormValues,
  useProductInput,
  getMaximumNumberOfOligosPerSession,
  isProbeProduct,
  checkForRedesignedBiTubeProduct,
  getBreadCrumb,
} from '../ProductsConfiguration'
import router, { useRouter } from 'next/router'
import NextStepButtonComponent from '../NextStepButtonComponent'
import useFormikHelper from '../useFormikHelper'
import MyOligosSideSection from '../MyOligosSideSection'
import AddOligoButton from '@src/routes/Configurators/components/LeftSideBottomBar/AddOligoButton'
import useTubeValidation from '../useTubeValidation'
import Step2 from '../Step2/ConfirmOrderRedesign'
import useCopyPasteHelper from '../useCopyPasteHelper'

import { getSessionDataFromFormikData } from '../SessionDataUtil'
import { checkModErrorsAndScroll, updatedData } from '../utils'
import CircleLoader from '@src/components/CircleLoader'
import { Modification } from '../../../utils/enum'
import { TubeProperties } from '../../../utils/StandardMTOEnum'
import { TubeEditLinkDetails } from '../../ConfiguratorSharedTypes'
import { useUserSession } from '@src/utils/useUserSession'
import {
  useBitubeEditLinkQuery,
  useTubeEditLinkQuery,
} from '@src/queries/ConfiguratorTubesQuery.generated'
import MaxOligoCountReachedModal from '../MaxOligoCountReachedModal'
import useTubeTotalValidation from '../useTubeTotalValidation'
import { BiTubeProduct } from '../../BiTube/enum'

const DnaTubeLayout = () => {
  // Product Level Layout. We can keep the product specific configurations here.
  const product = useProductFromQueryParam()
  const productTitle = getProductTitle(product)
  const formik = useFormikHelper()
  const { query } = useRouter()
  const { userSession } = useUserSession()
  const productDetails = useProductInput()
  const intl = useIntl()

  const initialValues = getInitialValuesBasedOnProduct(product)
  const activeLink = query.activeLink || ActiveLinkType.SequenceEntry
  const cartId = query.cartid || ''
  const itemId = query.itemid || ''

  const tubeEditLinkDetails: TubeEditLinkDetails = {
    cartId: cartId,
    cartItemId: itemId,
    store: userSession.store,
    productType: productDetails.productType as string,
    participantId: productDetails.participantId as string,
    countryCode: productDetails.countryCode as string,
  }

  const useProductEditLinkQuery = (
    tubeEditLinkDetails: TubeEditLinkDetails,
    userSession: { accessToken: string; store: string },
    cartId: string,
    itemId: string
  ): any => {
    const isBiTubeProduct = checkForRedesignedBiTubeProduct(
      tubeEditLinkDetails.productType
    )

    const tubeQueryResult = useTubeEditLinkQuery({
      variables: {
        product: tubeEditLinkDetails,
      },
      fetchPolicy: 'no-cache',
      skip: !userSession.accessToken || !(cartId && itemId) || isBiTubeProduct,
      ssr: false,
    })

    const bitubeQueryResult = useBitubeEditLinkQuery({
      variables: {
        product: tubeEditLinkDetails,
      },
      fetchPolicy: 'no-cache',
      skip: !userSession.accessToken || !(cartId && itemId) || !isBiTubeProduct,
      ssr: false,
    })

    if (isBiTubeProduct) {
      return {
        data: bitubeQueryResult.data?.bitubeEditLink?.data.filter(
          (data) => data.itemId === itemId
        ),
        loading: bitubeQueryResult.loading,
      }
    } else {
      return {
        data: tubeQueryResult.data?.tubeEditLink?.data.filter(
          (data) => data.itemId === itemId
        ),
        loading: tubeQueryResult.loading,
      }
    }
  }
  const { data: editData, loading: editDataLoading } = useProductEditLinkQuery(
    tubeEditLinkDetails,
    userSession,
    cartId,
    itemId
  )

  const { validationAPI, loading: validationAPILoading } = useTubeValidation()
  const { totalValidationAPI, loading: totalValidationAPILoading } =
    useTubeTotalValidation()
  const isBiTubeProduct = checkForRedesignedBiTubeProduct(product)
  const isMirna = product === BiTubeProduct.mirna

  const sequenceType = isBiTubeProduct
    ? TubeProperties.AntiSenseSequence
    : TubeProperties.Sequence
  const isEmptyOligoList =
    formik.allValues.oligoList.filter((data) => data?.[sequenceType]).length ===
    0

  const [loading, setLoading] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)

  const { copyPaste } = useCopyPasteHelper()
  const maximumNumberOfOligo = getMaximumNumberOfOligosPerSession(product)

  useEffect(() => {
    const oligoAlreadyAddedToFormik = formik.allValues.oligoList.find(
      (oligo) => oligo.itemId === itemId
    )
    const updateFormikWithEditData = async () => {
      if (!editData || editData.length === 0 || oligoAlreadyAddedToFormik) {
        return
      }
      const initData = initialValues.oligoList[0]
      const [editItem] = editData
      const updatedDataFromEdit = {
        ...initData,
        ...editItem,
        fiveModCategory: '',
        threeModCategory: '',
        internalModCategory: '',
      }
      if (isProbeProduct(product)) {
        updatedDataFromEdit['five-reporter-three-quencher'] =
          editItem.internalMod
            ? `${editItem.fiveMod}/${editItem.internalMod}/${editItem.threeMod}`
            : `${editItem.fiveMod}/${editItem.threeMod}`
      }

      formik.addItemToOligoList(
        updatedDataFromEdit as SingleTubeFormValues,
        true
      )

      formik.setFieldValue('activeOligoIndex', 0, false)
      if (editData?.[0]) {
        // isocode is not present on bitube products
        if (!isBiTubeProduct && 'isoCode' in editData[0]) {
          formik.setFieldValue('isoCode', editData[0].isoCode === true, false)
        }
        formik.setFieldValue('activeOligoIndex', 0, false)
      }
    }
    updateFormikWithEditData()
  }, [editData, itemId])

  useEffect(() => {
    const oligoWithError = formik.allValues.oligoList.find(
      (oligo) => oligo.listErrors?.length > 0
    )
    if (formik.allValues.displayPageError && !oligoWithError)
      formik.setFieldValue('displayPageError', false, false)
  }, [formik.allValues.oligoList])

  const baseUrl = `${checkForRedesignedBiTubeProduct(product) ? biTubeRoute.index() : standardTubeRoute.index()}?product=${product}`
  const sidebarItems = [
    {
      label: intl.formatMessage({
        id: 'CONF_SEQUENCE_ENTRY',
        defaultMessage: 'Sequence Entry',
      }),

      activeLink:
        activeLink === ActiveLinkType.SequenceEntry ||
        activeLink === ActiveLinkType.MultipleEntry,
      url: `${baseUrl}&activeLink=${ActiveLinkType.SequenceEntry}`,
      linkDisable: false,
    },
    {
      label: intl.formatMessage({
        id: 'CONFIRM_ORDER',
        defaultMessage: 'Confirm Order',
      }),
      activeLink: activeLink === ActiveLinkType.ConfirmOrder,
      url: `${baseUrl}&activeLink=${ActiveLinkType.ConfirmOrder}`,
      linkDisable: isEmptyOligoList,
    },
  ]

  const nextStepAction = async () => {
    formik.setFieldValue('hasTriedNextStep', true, false)
    formik.setFieldValue('activeValidationButton', 'nextStep')
    if (activeLink === ActiveLinkType.MultipleEntry) {
      copyPaste()
    } else {
      const errorList = await validationAPI({
        ...formik.validationFields,
      })
      formik.touchRequiredFields()
      if (errorList.length > 0) {
        const filteredErrorList = errorList.filter(
          ({ key }) => key !== 'ReviewError'
        )
        const hasOnlyModErrors = filteredErrorList.every(
          ({ key }) =>
            key.toLowerCase() === Modification.ThreeMod.toLowerCase() ||
            key.toLowerCase() === Modification.FiveMod.toLowerCase()
        )
        const hasEitherNameOrSequenceError = filteredErrorList.some(
          ({ key }) =>
            key.toLowerCase() === TubeProperties.Sequence.toLowerCase() ||
            key.toLowerCase() === TubeProperties.Name.toLowerCase()
        )

        const hasMiRnaNameError =
          filteredErrorList.some(
            ({ key }) => key.toLowerCase() === TubeProperties.Name.toLowerCase()
          ) && isMirna

        const hasMiRnaFiveModError =
          filteredErrorList.some(
            ({ key }) =>
              key.toLowerCase() === Modification.FiveMod.toLowerCase()
          ) && isMirna

        const hasEitherAntiSenseNameOrAntiSenseSequenceError =
          filteredErrorList.some(
            ({ key }) =>
              key.toLowerCase() ===
                TubeProperties.AntiSenseSequence.toLowerCase() ||
              key.toLowerCase() === TubeProperties.AntiSenseName.toLowerCase()
          )

        const hasOnlySpecificationErrors = filteredErrorList.every(
          ({ key }) =>
            key === TubeProperties.Concentration ||
            key === TubeProperties.Format ||
            key === TubeProperties.Purification ||
            key === TubeProperties.Scale ||
            key === TubeProperties.Amount
        )
        //there are errors in the active oligo, display page error
        formik.setOligoFieldValue('displayPageError', true, false)

        if (hasMiRnaNameError) {
          window.scrollTo(0, 700)
        } else if (hasMiRnaFiveModError) {
          window.scrollTo(0, 900)
          formik.setOligoFieldValue(
            'expandPrimarySequenceAccordion',
            true,
            false
          )
        } else if (hasEitherNameOrSequenceError) {
          window.scrollTo(0, 200)
        } else if (hasEitherAntiSenseNameOrAntiSenseSequenceError) {
          window.scrollTo(0, 400)
        } else if (hasOnlyModErrors) {
          window.scrollTo(0, 800)
          formik.setFieldValue('activeTab', 0)
        } else if (hasOnlySpecificationErrors && isMirna) {
          window.scrollTo(0, 1300)
        } else if (hasOnlySpecificationErrors) {
          window.scrollTo(0, 1100)
        } else {
          window.scrollTo(0, 200)
        }
      } else {
        //check for other oligos in list which has error
        const oligoWithError = formik.allValues.oligoList.findIndex(
          (oligo) => oligo.listErrors?.length > 0
        )
        if (oligoWithError !== -1) {
          formik.setFieldValue('displayPageError', true, false)
          formik.setOligoFieldValue('displayPageError', false, false)

          //if any other oligo has error, load it for editing
          formik.setFieldValue('activeOligoIndex', oligoWithError, false)
          router.replace(
            {
              pathname: router.pathname,
              query: router.query,
            },
            undefined,
            { shallow: true }
          )
        } else {
          // navigate to confirm page
          const { country, language, product } = router.query
          router.replace(
            {
              pathname: router.pathname,
              query: {
                country: country,
                language: language,
                product: product,
                activeLink: ActiveLinkType.ConfirmOrder,
              },
            },
            undefined,
            { shallow: true }
          )
        }
      }
    }
  }

  async function validateDataWithAPI(
    dataArray: SingleTubeFormValues[]
  ): Promise<SingleTubeFormValues[]> {
    const currentSessionData = getSessionDataFromFormikData(
      product,
      formik.allValues.oligoList
    )
    try {
      const tubeTotalValidationData =
        await totalValidationAPI(currentSessionData)

      if (tubeTotalValidationData) {
        const validatedData = updatedData(dataArray, tubeTotalValidationData)
        formik.setFieldValue(
          'oligoList',
          updatedData(dataArray, tubeTotalValidationData)
        )
        return validatedData
      } else return []
    } catch (error) {
      console.error('Error validating item }', error)
      return []
    }
  }
  const handleAddOligo = async () => {
    formik.setFieldValue('hasTriedNextStep', true, false)
    formik.setFieldValue('activeValidationButton', 'addOligo')

    if (activeLink === ActiveLinkType.ConfirmOrder) {
      if (formik.allValues.oligoList?.length === maximumNumberOfOligo) {
        //open modal
        setDialogOpen(true)
      } else {
        formik.addItemToOligoList(initialValues.oligoList[0])
        formik.setFieldValue('hasTriedNextStep', false, false)
        router.replace(
          {
            pathname: router.pathname,
            query: {
              activeLink: ActiveLinkType.SequenceEntry,
              country: router.query.country,
              product: router.query.product,
              language: router.query.language,
            },
          },
          undefined,
          { shallow: true }
        )
      }
    } else {
      setLoading(true)
      await validateAndUpdateOligos(
        formik.allValues.oligoList,
        initialValues.oligoList[0]
      )
      checkModErrorsAndScroll({
        validationObj: formik.errors,
        isBiTube: checkForRedesignedBiTubeProduct(product),
      })
      setLoading(false)
    }
  }

  async function validateAndUpdateOligos(
    dataArray: SingleTubeFormValues[],
    newItem: SingleTubeFormValues
  ) {
    try {
      const validatedData = await validateDataWithAPI(dataArray)

      const allOligosValid = validatedData.every(
        (item) => !item.displayPageError
      )

      if (allOligosValid) {
        //check for maximum number of oligos and if reached display error.
        if (validatedData.length === maximumNumberOfOligo) {
          //open modal
          setDialogOpen(true)
          return true
        } else {
          const updatedList = [...validatedData, newItem]
          formik.addItemToOligoList(newItem)
          formik.setFieldValue(
            'activeOligoIndex',
            updatedList.length - 1,
            false
          )
          formik.setFieldValue('hasTriedNextStep', false, false)
          router.replace(
            {
              pathname: router.pathname,
              query: {
                ...router.query,
                oligoIndex: updatedList.length - 1,
              },
            },
            undefined,
            { shallow: true }
          )

          return true
        }
      } else {
        const errorIndex = validatedData.findIndex(
          (item) => item.displayPageError
        )
        formik.setFieldValue('activeOligoIndex', errorIndex, false)
        router.replace(
          {
            pathname: router.pathname,
            query: router.query,
          },
          undefined,
          { shallow: true }
        )
        return false
      }
    } catch (error) {
      console.error('Error in validateAndUpdateOligos:', error)
      return false
    }
  }

  useEffect(() => {
    if (formik.selectedOligoValues.displayPageError) {
      formik.touchRequiredFields()
      validationAPI({
        ...formik.validationFields,
      })
    }
  }, [formik.activeOligoIndex, formik.selectedOligoValues.displayPageError])

  useEffect(() => {
    const hasRequiredFieldErrors =
      formik.errors &&
      Object.values(formik.errors).some((error) => error !== '')

    if (!hasRequiredFieldErrors) {
      window.scrollTo(0, 0)
    }
  }, [formik.activeOligoIndex])

  const disableAddNextLinks = !(
    formik.touched?.name ||
    formik.touched?.[sequenceType] ||
    formik.selectedOligoValues.name !== '' ||
    formik.selectedOligoValues?.[sequenceType] !== ''
  )
  const breadcrumbItems = getBreadCrumb(product)

  return (
    <>
      <ConfiguratorLayout
        title={productTitle}
        sidebarItems={sidebarItems}
        breadcrumbItems={breadcrumbItems}
        bodyContent={
          <>
            <MaxOligoCountReachedModal
              maxOligoCount={maximumNumberOfOligo}
              dialogOpen={dialogOpen}
              handleClose={() => setDialogOpen(false)}
            />
            {activeLink === ActiveLinkType.SequenceEntry ||
            activeLink === ActiveLinkType.MultipleEntry ? (
              <Step1 />
            ) : activeLink === ActiveLinkType.ConfirmOrder ? (
              <Step2 handleAddOligo={handleAddOligo} />
            ) : (
              <></>
            )}
          </>
        }
        spaceOutButtons
        tabPadding
        sidebarBottom={
          activeLink === TabsEnum.SequenceEntry ? (
            <MyOligosSideSection
              handleAddOligo={handleAddOligo}
              disableAddNextLinks={disableAddNextLinks}
            />
          ) : (
            <></>
          )
        }
        previousStepButton={
          activeLink === TabsEnum.SequenceEntry ? (
            <AddOligoButton
              handleAddOligo={handleAddOligo}
              disabled={disableAddNextLinks}
            />
          ) : (
            <></>
          )
        }
        nextStepButton={
          <NextStepButtonComponent
            nextStepAction={nextStepAction}
            disableAddNextLinks={disableAddNextLinks}
          />
        }
      />
      {(loading ||
        editDataLoading ||
        validationAPILoading ||
        totalValidationAPILoading) && <CircleLoader />}
    </>
  )
}

export default DnaTubeLayout
